import { CloseOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import React, { useMemo, useRef, useState } from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { useOnClickOutside } from '../../../../hooks/use_on_click_outside';
import {
  CORE_LAYER,
  CORE_LAYER_INFO,
  isCoreLayer,
  isWeatherLayer,
  MapLayerState,
  WEATHER_LAYER,
  WEATHER_LAYER_INFO,
} from '../../map_types';
import { PinHighActivity } from '../../pin_high_activity';
import { PinLowActivity } from '../../pin_low_activity';
import airTemp from './layer_color_scales/airTemp.svg';
import depth from './layer_color_scales/depthContours.svg';
import hardness from './layer_color_scales/hardnessContours.svg';
import clarity from './layer_color_scales/waterClarity.svg';
import waterTemp from './layer_color_scales/waterTemp.svg';
import wind from './layer_color_scales/wind.svg';
import styles from './legend_modal.less';

const CONTOUR_HARDNESS = () => (
  <div className={styles.tabContentContainer}>
    <p className={styles.tabHeading}>Hardness Contours</p>
    <div>
      <div className={styles.row__spaceBetween}>
        <span className={styles.colorScaleText}>SOFTEST</span>
        <span className={styles.colorScaleText}>SOFT</span>
        <span className={styles.colorScaleText}>HARD</span>
        <span className={styles.colorScaleText}>HARDEST</span>
      </div>
      <img src={hardness} alt="hardness" className={styles.colorScale__hardness} />
      <div className={styles.row__contours}>
        <span className={styles.contourColorBox} style={{ background: '#fcffcd' }} />
        <span className={styles.contourDescription}>Softest bottom, silt, muck, etc.</span>
      </div>
      <div className={styles.row__contours}>
        <span className={styles.contourColorBox} style={{ background: '#F4EF98' }} />

        <span className={styles.contourDescription}>
          Soft, but not softest bottom (usually a mixed composition)
        </span>
      </div>
      <div className={styles.row__contours}>
        <span className={styles.contourColorBox} style={{ background: '#FB9F69' }} />
        <span className={styles.contourDescription}>Hard, but not hardest bottom</span>
      </div>
      <div className={styles.row__contours}>
        <span className={styles.contourColorBox} style={{ background: '#FB6767' }} />
        <span className={styles.contourDescription}>
          Hardest bottom (bedrock, rubble, gravel, shellbed)
        </span>
      </div>
    </div>
  </div>
);

const WATER_TEMP = () => (
  <div className={styles.tabContentContainer}>
    <p className={styles.tabHeading}>Water Temperature</p>
    <div>
      <span className={styles.row__spaceBetween}>
        <span className={styles.colorScaleText}>COLDEST</span>
        <span className={styles.colorScaleText}>°F</span>
        <span className={styles.colorScaleText}>WARMEST</span>
      </span>
      <img src={waterTemp} alt="waterTemp" className={styles.colorScale} />
      <div className={styles.row__spaceBetween}>
        <span className={styles.legendScaleNumbers}>&lt;35º</span>
        <span className={styles.legendScaleNumbers}>&gt;80º</span>
      </div>
    </div>
  </div>
);

const CONTOUR_DEPTH = () => (
  <div className={styles.tabContentContainer}>
    <p className={styles.tabHeading}>Water Depth</p>
    <div>
      <span className={styles.row__spaceBetween}>
        <span className={styles.colorScaleText}>SHALLOW</span>
        <span className={styles.colorScaleText}>DEEP</span>
      </span>
      <img src={depth} alt="depth" className={styles.colorScale} />
    </div>
  </div>
);

const CLARITY = () => (
  <div className={styles.tabContentContainer}>
    <p className={styles.tabHeading}>Water Clarity</p>
    <div>
      <span className={styles.row__spaceBetween}>
        <span className={styles.colorScaleText}>CLEAR</span>
        <span className={styles.colorScaleText}>MURKY</span>
      </span>
      <img src={clarity} alt="clarity" className={styles.colorScale} />
      <div className={styles.row__spaceBetween}>
        <span className={styles.legendScaleNumbers}>0</span>
        <span className={styles.legendScaleNumbers}>1</span>
        <span className={styles.legendScaleNumbers}>2</span>
        <span className={styles.legendScaleNumbers}>3</span>
        <span className={styles.legendScaleNumbers}>4</span>
        <span className={styles.legendScaleNumbers}>5</span>
        <span className={styles.legendScaleNumbers}>6</span>
        <span className={styles.legendScaleNumbers}>7</span>
        <span className={styles.legendScaleNumbers}>8</span>
        <span className={styles.legendScaleNumbers}>9</span>
        <span className={styles.legendScaleNumbers}>10</span>
      </div>
    </div>
  </div>
);

const AIR_TEMP = () => (
  <div className={styles.tabContentContainer}>
    <p className={styles.tabHeading}>Air Temperature</p>
    <div>
      <span className={styles.row__spaceBetween}>
        <span className={styles.colorScaleText}>COLDEST</span>
        <span className={styles.colorScaleText}>°F</span>
        <span className={styles.colorScaleText}>WARMEST</span>
      </span>
      <img src={airTemp} alt="airTemp" className={styles.colorScale} />
      <div className={styles.row__spaceBetween}>
        <span className={styles.legendScaleNumbers}>-60</span>
        <span className={styles.legendScaleNumbers}>-40</span>
        <span className={styles.legendScaleNumbers}>-20</span>
        <span className={styles.legendScaleNumbers}>0</span>
        <span className={styles.legendScaleNumbers}>20</span>
        <span className={styles.legendScaleNumbers}>40</span>
        <span className={styles.legendScaleNumbers}>60</span>
        <span className={styles.legendScaleNumbers}>80</span>
        <span className={styles.legendScaleNumbers}>100</span>
        <span className={styles.legendScaleNumbers}>120</span>
      </div>
    </div>
  </div>
);

const WIND = () => (
  <div className={styles.tabContentContainer}>
    <p className={styles.tabHeading}>Wind</p>
    <div>
      <span className={styles.row__spaceBetween}>
        <span className={styles.colorScaleText}>SLOWEST</span>
        <span className={styles.colorScaleText}>mph</span>
        <span className={styles.colorScaleText}>FASTEST</span>
      </span>
      <img src={wind} alt="wind" className={styles.colorScale} />
      <div className={styles.row__spaceBetween}>
        <span className={styles.legendScaleNumbers}>0</span>
        <span className={styles.legendScaleNumbers}>10</span>
        <span className={styles.legendScaleNumbers}>20</span>
        <span className={styles.legendScaleNumbers}>40</span>
        <span className={styles.legendScaleNumbers}>60</span>
        <span className={styles.legendScaleNumbers}>80</span>
        <span className={styles.legendScaleNumbers}>100</span>
      </div>
    </div>
  </div>
);

const LegendSubComponents = {
  AIR_TEMP,
  CLARITY,
  CONTOUR_DEPTH,
  CONTOUR_HARDNESS,
  WATER_TEMP,
  OMNIA_WATER_TEMP: WATER_TEMP,
  WIND,
};

interface LegendModalProps {
  isModalOpen: boolean;
  onModalChange: (isModalOpen: boolean) => void;
  mapLayerState: MapLayerState;
}

export const LegendModal = React.memo((props: LegendModalProps) => {
  const { isModalOpen, onModalChange, mapLayerState } = props;
  const modalRef = useRef();

  useOnClickOutside(modalRef, () => {
    if (isModalOpen) {
      onModalChange(false);
    }
  });

  const [tabIndex, setTabIndex] = useState(0);
  const handleSelect = (index: number) => {
    setTabIndex(index);
  };

  const legendLayers = useMemo(() => {
    const layersArray = [];
    if (mapLayerState.CORE) {
      if (mapLayerState.CORE in LegendSubComponents) {
        layersArray.push(mapLayerState.CORE);
      }
    }
    if (mapLayerState.WEATHER.length) {
      mapLayerState.WEATHER.forEach((layer) => {
        if (layer in LegendSubComponents) {
          layersArray.push(layer);
        }
      });
    }
    return layersArray as CORE_LAYER[] | WEATHER_LAYER[];
  }, [mapLayerState.CORE, mapLayerState.WEATHER]);

  const hasMultipleLayers = legendLayers.length > 1;

  if (!isModalOpen) {
    return null;
  }
  return (
    <>
      <div className={styles.container} ref={modalRef}>
        <div className={styles.modalHeader}>
          <span className={styles.heading}>Legend</span>
          <CloseOutlined onClick={() => onModalChange(false)} />
        </div>
        <div className={styles.modalContent}>
          <section className={styles.pinsSection}>
            <div className={styles.pinsContainer}>
              <p>Pins</p>
              <div className={'grid grid-cols-5 gap-1 items-end'}>
                <div className={styles.pin}>
                  <PinLowActivity className={styles.pinLessActive} />
                  <span>Less Active</span>
                </div>
                <div className={styles.pin}>
                  <PinHighActivity favorite={false} />
                  <span>Most Active</span>
                </div>
                <div className={styles.pin}>
                  <PinHighActivity favorite={true} />
                  <span>Your Favorites</span>
                </div>
                <div className={styles.pin}>
                  <PinHighActivity lunkerPotential={true} />
                  <span>Lunker Potential</span>
                </div>
                <div className={styles.pin}>
                  <PinHighActivity hasSurveyData={true} />
                  <span>Species Survey</span>
                </div>
              </div>
            </div>
          </section>
          <section
            className={classNames(styles.layersContainer, {
              [styles.layersContainer__borderTop]: hasMultipleLayers,
            })}
          >
            {hasMultipleLayers ? (
              <Tabs
                forceRenderTabPanel
                selectedTabClassName={styles.tab__active}
                selectedIndex={tabIndex}
                onSelect={handleSelect}
              >
                <TabList className={styles.tabs}>
                  {legendLayers.map((layer, index) => {
                    let icon = null;
                    if (isCoreLayer(layer)) {
                      icon = CORE_LAYER_INFO[layer].tabsIcon;
                    } else if (isWeatherLayer(layer)) {
                      icon = WEATHER_LAYER_INFO[layer].tabsIcon;
                    }

                    return (
                      <Tab
                        key={index}
                        className={classNames(styles.tab, {
                          [styles.tab__wind]: layer === WEATHER_LAYER.WIND,
                          [styles.tab__airTemp]: layer === WEATHER_LAYER.AIR_TEMP,
                          [styles.tab__waterTemp]: layer === CORE_LAYER.OMNIA_WATER_TEMP,
                        })}
                      >
                        {icon()}
                      </Tab>
                    );
                  })}
                </TabList>
                {legendLayers.map((l, index) => (
                  <TabPanel key={index} className={styles.tabContent}>
                    {(LegendSubComponents as any)[l]()}
                  </TabPanel>
                ))}
              </Tabs>
            ) : (
              (LegendSubComponents as any)[legendLayers[0]]?.()
            )}
          </section>
        </div>
      </div>
      <div
        onClick={(e) => {
          e.stopPropagation();
          onModalChange(true);
        }}
        className={styles.mobileLegendButton}
      >
        Legend
      </div>
    </>
  );
});
